import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merge Tailwind CSS classes with clsx
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format date for display
 */
export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format date with time
 */
export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Get pocket depth severity class
 */
export function getPocketDepthClass(depth: number | null): string {
  if (depth === null) return '';
  if (depth <= 3) return 'healthy';
  if (depth === 4) return 'mild';
  if (depth === 5) return 'moderate';
  return 'severe';
}

/**
 * Get pocket depth color for charts
 */
export function getPocketDepthColor(depth: number | null): string {
  if (depth === null) return '#9ca3af'; // gray
  if (depth <= 3) return '#22c55e'; // green
  if (depth === 4) return '#eab308'; // yellow
  if (depth === 5) return '#f97316'; // orange
  return '#ef4444'; // red
}

/**
 * Calculate age from date of birth
 */
export function calculateAge(dob: string | null): number | null {
  if (!dob) return null;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

/**
 * Get initials from name
 */
export function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, length: number): string {
  if (text.length <= length) return text;
  return text.slice(0, length) + '...';
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Get risk level badge class
 */
export function getRiskLevelClass(level: string | null): string {
  switch (level) {
    case 'low':
      return 'badge-success';
    case 'moderate':
      return 'badge-warning';
    case 'high':
      return 'badge-error';
    default:
      return 'badge-info';
  }
}

/**
 * Get chart status badge class
 */
export function getChartStatusClass(status: string): string {
  switch (status) {
    case 'finalized':
      return 'badge-success';
    case 'draft':
      return 'badge-warning';
    case 'amended':
      return 'badge-info';
    default:
      return 'badge-info';
  }
}
